import type { ScheduleOption } from '../graphqlEnums'
import type { ShareMode } from './graphqlTypes'

import { getShareMode } from '../utils/getShareMode'
import type { MapperContext } from './types'

/**
 * Edit-only scheduling resolution.
 *
 * On edit the schedule selector always opens on `FixedTime`, seeded with the
 * post's current send time — including the queue-derived `dueAt` of a "next
 * available" post, which the scheduling engine assigned rather than the user.
 * Mapping that untouched selection straight to `customScheduled` would pin the
 * post to a custom time it never had.
 *
 * So we emit a scheduling change only when the user actually changed it. When
 * nothing changed we return `{}`, omitting `mode`/`dueAt`; the server then leaves
 * the post's share mode, queue slot, and any custom time exactly as stored (see
 * `editPostNatively`: an absent mode skips scheduling resolution entirely). This
 * mirrors the legacy REST path's `getShareModeChange` guard.
 */
export function resolveEditScheduling(ctx: MapperContext): {
  mode?: ShareMode
  dueAt?: string
} {
  const option = ctx.scheduleOption
  const currentScheduledAt = ctx.customScheduleTime ?? null
  const initialScheduledAt = ctx.initialScheduledAt ?? null

  // The selector seeds `FixedTime` whenever the post carried a send time;
  // otherwise it opens on the queue option.
  const initialOption: ScheduleOption = initialScheduledAt
    ? 'FixedTime'
    : 'Queue'

  const optionChanged = option !== initialOption
  const fixedTimeChanged =
    option === 'FixedTime' &&
    initialOption === 'FixedTime' &&
    currentScheduledAt !== initialScheduledAt

  if (!optionChanged && !fixedTimeChanged) {
    return {}
  }

  const mode = getShareMode(option)

  // Legacy `customScheduleTime` is unix seconds; `dueAt` is an ISO DateTime.
  const dueAt =
    option === 'FixedTime' && currentScheduledAt != null
      ? new Date(currentScheduledAt * 1000).toISOString()
      : undefined

  return { mode, ...(dueAt ? { dueAt } : {}) }
}
